import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./context/AuthContext";
import { WishlistProvider } from "./context/WishlistContext";
import { SettingsProvider } from "./context/SettingsContext";
import { CartProvider } from "./context/CartContext";

import PublicLayout from "./components/PublicLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import CartDrawer from "./components/CartDrawer";
import FloatingWhatsApp from "./components/FloatingWhatsApp";
import WalkingDuck from "./components/WalkingDuck";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Collections from "./pages/Collections";
import ProductDetail from "./pages/ProductDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Account from "./pages/Account";
import Wishlist from "./pages/Wishlist";
import SearchPage from "./pages/SearchPage";
import NotFound from "./pages/NotFound";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import AuthCallback from "./pages/AuthCallback";
import MagicLinkVerify from "./pages/MagicLinkVerify";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import { isEmergentAuthEnabled } from "./lib/featureFlags";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminOverview from "./pages/admin/AdminOverview";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminMedia from "./pages/admin/AdminMedia";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminPolicies from "./pages/admin/AdminPolicies";

import { pageView } from "./lib/analytics";

function RouteTracker() {
    const loc = useLocation();
    useEffect(() => {
        pageView(loc.pathname, document.title);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [loc.pathname]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    // Detect Emergent Google OAuth callback (synchronous check during render)
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isEmergentAuthEnabled() && location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={wrap(Home)} />
            <Route path="/shop" element={wrap(Shop)} />
            <Route path="/collections" element={wrap(Collections)} />
            <Route path="/category/:slug" element={wrap(Shop)} />
            <Route path="/product/:slug" element={wrap(ProductDetail)} />
            <Route path="/about" element={wrap(About)} />
            <Route path="/contact" element={wrap(Contact)} />
            <Route path="/login" element={wrap(Login)} />
            <Route path="/forgot-password" element={wrap(ForgotPassword)} />
            <Route path="/auth/reset-password" element={wrap(ResetPassword)} />
            <Route path="/signup" element={wrap(Signup)} />
            <Route path="/wishlist" element={wrap(Wishlist)} />
            <Route path="/search" element={wrap(SearchPage)} />
            <Route path="/auth/magic" element={wrap(MagicLinkVerify)} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            <Route path="/checkout" element={
                <ProtectedRoute>
                    <PublicLayout><Checkout /></PublicLayout>
                </ProtectedRoute>
            } />
            <Route path="/order/:id" element={
                <ProtectedRoute>
                    <PublicLayout><OrderConfirmation /></PublicLayout>
                </ProtectedRoute>
            } />
            <Route path="/account" element={
                <ProtectedRoute>
                    <PublicLayout><Account /></PublicLayout>
                </ProtectedRoute>
            } />

            <Route path="/admin/login" element={<PublicLayout><Login admin /></PublicLayout>} />
            <Route path="/admin" element={
                <ProtectedRoute role="admin">
                    <AdminLayout />
                </ProtectedRoute>
            }>
                <Route index element={<AdminOverview />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="policies" element={<AdminPolicies />} />
                <Route path="settings" element={<AdminSettings />} />
                <Route path="analytics" element={<AdminAnalytics />} />
            </Route>

            <Route path="*" element={wrap(NotFound)} />
        </Routes>
    );
}

const wrap = (Component) => (
    <PublicLayout>
        <Component />
    </PublicLayout>
);

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <SettingsProvider>
                    <AuthProvider>
                        <WishlistProvider>
                            <CartProvider>
                                <Toaster position="top-center" toastOptions={{ style: { background: "#FFFDF8", color: "#2D2A26", border: "1px solid #CFDBBE", fontFamily: "'Outfit', sans-serif" } }} />
                                <RouteTracker />
                                <AppRouter />
                                <CartDrawer />
                                <FloatingWhatsAppRouteAware />
                                <WalkingDuckRouteAware />
                            </CartProvider>
                        </WishlistProvider>
                    </AuthProvider>
                </SettingsProvider>
            </BrowserRouter>
        </div>
    );
}

// Hide floating button on admin/checkout pages
function FloatingWhatsAppRouteAware() {
    const loc = useLocation();
    if (loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/checkout")) return null;
    return <FloatingWhatsApp />;
}

function WalkingDuckRouteAware() {
    const loc = useLocation();
    if (loc.pathname.startsWith("/admin")) return null;
    return <WalkingDuck />;
}

export default App;
